(function () {
  const root = document.querySelector('[data-ja-reviews]');
  if (!root) return;

  const state = {
    productId: root.dataset.productId,
    handle: root.dataset.productHandle,
    productImage: root.dataset.productImage || '',
    productTitle: root.dataset.productTitle || '',
    endpoint: root.dataset.endpoint,
    reviews: [],
    aggregates: null,
    filters: { rating: 'all', fit: 'all', width: 'all' },
    sort: 'newest',
    page: 0,
    pageSize: 6,
    ugcImages: [],
    ugcPreviewCount: 3,
  };

  const qs = (s, r = root) => r.querySelector(s);
  const qsa = (s, r = root) => [...r.querySelectorAll(s)];

  async function load() {
    const stub = root.querySelector('[data-ja-reviews-stub]');
    if (stub) {
      try {
        const data = JSON.parse(stub.textContent);
        state.reviews = data.reviews || [];
        state.aggregates = data.aggregates || null;
        render();
        return;
      } catch (e) { console.error('[ja-reviews] stub parse error', e); }
    }
    if (!state.endpoint) {
      renderEmpty('No reviews endpoint configured.');
      return;
    }
    try {
      const url = state.endpoint + (state.endpoint.includes('?') ? '&' : '?') + 'product_id=' + encodeURIComponent(state.productId);
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      state.reviews = data.reviews || [];
      state.aggregates = data.aggregates || null;
      render();
    } catch (e) {
      console.error('[ja-reviews] fetch failed', e);
      renderEmpty('Reviews unavailable right now.');
    }
  }

  function render() {
    renderAggregates();
    renderFilters();
    renderUGC();
    renderList();
    syncHeaderRatingWidget();
  }

  // The theme renders Klaviyo's `<span class="klaviyo-star-rating-widget">` near
  // the product title. Klaviyo only counts its own reviews, so any product with
  // Amazon reviews but no Klaviyo reviews shows "0 Reviews" up top while the
  // merged section below shows a real number. Override the widget contents with
  // our merged aggregates (and keep re-applying if Klaviyo writes over us).
  let headerWidgetObserver = null;
  function syncHeaderRatingWidget() {
    if (!state.aggregates) return;
    const total = state.aggregates.total ?? state.reviews.length;
    const avg = state.aggregates.avg_rating ?? 0;
    const widgets = [...document.querySelectorAll(
      '.klaviyo-star-rating-widget[data-id="' + state.productId + '"]'
    )];
    if (!widgets.length) {
      const fallback = document.querySelector('.klaviyo-star-rating-widget');
      if (fallback) widgets.push(fallback);
    }
    if (!widgets.length) return;

    const label = total === 0
      ? 'No reviews yet'
      : total + (total === 1 ? ' Review' : ' Reviews');
    const fillPct = (Math.max(0, Math.min(5, avg)) / 5 * 100) + '%';
    const html = '<a href="#" class="ja-header-rating" data-ja-header-rating>'
      + '<span class="ja-header-rating__stars" style="--star-fill:' + fillPct + '" aria-hidden="true">★★★★★</span>'
      + '<span class="ja-header-rating__count">' + label + '</span>'
      + '</a>';

    const apply = () => {
      widgets.forEach(w => {
        if (w.getAttribute('data-ja-rating-html') === html) return;
        w.innerHTML = html;
        w.setAttribute('data-ja-rating-html', html);
        w.style.display = 'inline-flex';
        const link = w.querySelector('[data-ja-header-rating]');
        if (link) link.addEventListener('click', (e) => {
          e.preventDefault();
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    };
    apply();

    if (headerWidgetObserver) headerWidgetObserver.disconnect();
    headerWidgetObserver = new MutationObserver(() => apply());
    widgets.forEach(w => headerWidgetObserver.observe(w, { childList: true, subtree: true }));
  }

  function renderAggregates() {
    if (!state.aggregates) return;
    const a = state.aggregates;
    const avg = a.avg_rating ?? 0;
    qs('[data-avg-rating-num]').textContent = avg.toFixed(1);
    const starsEl = qs('[data-avg-rating-stars]');
    if (starsEl) starsEl.style.setProperty('--star-fill', (avg / 5 * 100) + '%');
    qs('[data-total-count]').textContent = a.total ?? state.reviews.length;

    const breakdown = a.rating_breakdown || state.reviews.reduce((acc, r) => {
      acc[r.rating] = (acc[r.rating] || 0) + 1; return acc;
    }, {});
    const brTotal = Object.values(breakdown).reduce((s, n) => s + (n || 0), 0) || 1;
    for (let n = 1; n <= 5; n++) {
      const count = Number(breakdown[n] || breakdown[String(n)] || 0);
      const bar = qs('[data-star-bar="' + n + '"]');
      const cnt = qs('[data-star-count="' + n + '"]');
      if (bar) bar.style.width = (count / brTotal * 100) + '%';
      if (cnt) cnt.textContent = count;
    }

    // Thumb-based fit slider (position = weighted average 0-100%, center when no data)
    const fit = a.fit || { small: 0, tts: 0, large: 0 };
    const fitTotal = fit.small + fit.tts + fit.large;
    const fitPos = fitTotal ? ((fit.small * 0 + fit.tts * 50 + fit.large * 100) / fitTotal) : 50;
    qs('[data-agg="fit"] [data-thumb]').style.left = fitPos + '%';

    // Thumb-based width slider
    const w = a.width || { tight: 0, tts: 0, loose: 0 };
    const wTotal = w.tight + w.tts + w.loose;
    const wPos = wTotal ? ((w.tight * 0 + w.tts * 50 + w.loose * 100) / wTotal) : 50;
    qs('[data-agg="width"] [data-thumb]').style.left = wPos + '%';

    // Sizing callout — hide entirely when no data
    const s = a.sizing || { sized_down: 0, same: 0, sized_up_half: 0, sized_up_full: 0 };
    const sTotal = s.sized_down + s.same + s.sized_up_half + s.sized_up_full;
    const sizingArticle = qs('[data-sizing-article]');
    if (!sTotal) {
      sizingArticle.hidden = true;
    } else {
      sizingArticle.hidden = false;
      const sizedUp = s.sized_up_half + s.sized_up_full;
      const sizedUpPct = Math.round(sizedUp / sTotal * 100);
      if (sizedUpPct >= 40) {
        qs('[data-sized-up]').textContent = sizedUpPct + '%';
        qs('[data-sized-up-amount]').textContent = s.sized_up_full > s.sized_up_half ? 'one full' : 'half a';
        qs('[data-sizing-sub]').textContent = Math.round(s.same / sTotal * 100) + '% order their usual size';
      } else {
        qs('[data-sizing-callout]').innerHTML = '<span data-sized-up>' + Math.round(s.same / sTotal * 100) + '%</span> order their usual size';
        qs('[data-sizing-sub]').textContent = sizedUp > 0 ? sizedUpPct + '% size up · ' + Math.round(s.sized_down / sTotal * 100) + '% size down' : '';
      }
    }
    qs('[data-ja-reviews-aggregates]').hidden = false;
  }

  function renderUGC() {
    const ugcEl = qs('[data-ja-reviews-ugc]');
    const grid = qs('[data-ugc-grid]');
    if (!ugcEl || !grid) return;

    // Collect all images from reviews (flatten, dedupe, preserve order)
    const seen = new Set();
    const all = [];
    state.reviews.forEach(r => {
      (r.images || []).forEach(src => {
        if (src && !seen.has(src)) { seen.add(src); all.push(src); }
      });
    });
    state.ugcImages = all;

    ugcEl.hidden = false;
    grid.innerHTML = '';

    // No photos: render a single large upload tile spanning the grid
    if (!all.length) {
      ugcEl.setAttribute('data-ugc-empty', '');
      const up = document.createElement('button');
      up.type = 'button';
      up.className = 'ja-ugc__upload';
      up.setAttribute('aria-label', 'Upload your photos here');
      up.addEventListener('click', openReviewForm);
      attachUploadTooltip(up, 'Upload your photos here');
      grid.appendChild(up);
      return;
    }
    ugcEl.removeAttribute('data-ugc-empty');

    // Render every photo into the scroll track. 3 fit in the visible window;
    // the rest overflow horizontally and are reached via the carousel arrows.
    grid.setAttribute('data-count', String(all.length));
    all.forEach((src, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ja-ugc__thumb';
      btn.style.backgroundImage = 'url(' + src + ')';
      btn.setAttribute('aria-label', 'View customer photo ' + (i + 1));
      btn.addEventListener('click', () => openLightbox(i));
      grid.appendChild(btn);
    });
    updateUgcCarousel();
  }

  // ─── UGC carousel (horizontal scroll + prev/next nav) ──────────────
  function updateUgcCarousel() {
    const carousel = qs('[data-ugc-carousel]');
    const scroller = qs('[data-ugc-scroll]');
    const prev = qs('[data-ugc-prev]');
    const next = qs('[data-ugc-next]');
    if (!carousel || !scroller) return;

    const hasMore = scroller.scrollWidth - scroller.clientWidth > 1;
    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

    // Reserve layout space for arrows always so thumbs don't resize when
    // prev appears — toggle visibility rather than display.
    carousel.toggleAttribute('data-has-more', hasMore);
    prev.style.visibility = hasMore && !atStart ? 'visible' : 'hidden';
    next.style.visibility = hasMore && !atEnd ? 'visible' : 'hidden';
  }

  // ─── Upload tile tooltip (mirrors zest-swatch-tooltips behaviour) ──
  let uploadTipEl = null;
  function ensureUploadTip() {
    if (uploadTipEl) return uploadTipEl;
    uploadTipEl = document.createElement('div');
    uploadTipEl.id = 'jaUploadTip';
    document.body.appendChild(uploadTipEl);
    return uploadTipEl;
  }
  function attachUploadTooltip(el, text) {
    const tip = ensureUploadTip();
    el.addEventListener('mouseenter', () => { tip.textContent = text; tip.style.display = 'block'; });
    el.addEventListener('mouseleave', () => { tip.style.display = 'none'; });
    el.addEventListener('mousemove', (e) => {
      if (tip.style.display !== 'block') return;
      tip.style.left = (e.clientX - tip.offsetWidth / 2) + 'px';
      tip.style.top = (e.clientY + 12) + 'px';
    });
  }

  function openReviewForm() {
    try {
      if (window.klaviyo && typeof window.klaviyo.openReviewForm === 'function') {
        window.klaviyo.openReviewForm(state.productId);
        return;
      }
    } catch (e) { /* fall through */ }
    // Fallback: click the existing Klaviyo badge near the product title
    const badge = document.querySelector('.kl_reviews__star_rating_widget, [data-kl-reviews-trigger], .klaviyo-reviews__badge');
    if (badge) { badge.click(); return; }
    console.warn('[ja-reviews] Klaviyo review form not available');
  }

  // ─── Lightbox ──────────────────────────────────────────────────
  let lbIdx = 0;
  function openLightbox(index) {
    if (!state.ugcImages.length) return;
    lbIdx = Math.max(0, Math.min(index, state.ugcImages.length - 1));
    paintLightbox();
    const lb = qs('[data-ja-lightbox]');
    lb.hidden = false;
    lb.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeLightbox() {
    const lb = qs('[data-ja-lightbox]');
    lb.hidden = true;
    lb.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
  function paintLightbox() {
    qs('[data-lightbox-img]').src = state.ugcImages[lbIdx];
    qs('[data-lightbox-index]').textContent = lbIdx + 1;
    qs('[data-lightbox-total]').textContent = state.ugcImages.length;
  }
  function navLightbox(delta) {
    lbIdx = (lbIdx + delta + state.ugcImages.length) % state.ugcImages.length;
    paintLightbox();
  }
  qs('[data-lightbox-close]')?.addEventListener('click', closeLightbox);
  qs('[data-lightbox-prev]')?.addEventListener('click', () => navLightbox(-1));
  qs('[data-lightbox-next]')?.addEventListener('click', () => navLightbox(1));

  // ─── UGC carousel wiring ─────────────────────────────────────────
  (() => {
    const scroller = qs('[data-ugc-scroll]');
    if (!scroller) return;
    qs('[data-ugc-prev]')?.addEventListener('click', () => {
      scroller.scrollBy({ left: -scroller.clientWidth, behavior: 'smooth' });
    });
    qs('[data-ugc-next]')?.addEventListener('click', () => {
      scroller.scrollBy({ left: scroller.clientWidth, behavior: 'smooth' });
    });
    scroller.addEventListener('scroll', updateUgcCarousel, { passive: true });
    window.addEventListener('resize', updateUgcCarousel, { passive: true });
  })();
  qs('[data-ja-lightbox]')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeLightbox();
  });
  document.addEventListener('keydown', (e) => {
    if (qs('[data-ja-lightbox]')?.hidden) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') navLightbox(-1);
    if (e.key === 'ArrowRight') navLightbox(1);
  });

  function applyFilter(field, val) {
    state.filters[field] = val;
    state.page = 0;
    // Keep desktop chip state in sync
    const chipset = qs('.ja-chipset[data-chipset="' + field + '"]');
    if (chipset) {
      qsa('.ja-chip', chipset).forEach(c => {
        const cv = c.dataset['filter' + field.charAt(0).toUpperCase() + field.slice(1)];
        c.classList.toggle('is-active', cv === val);
      });
    }
    // Keep mobile select state in sync
    const mSel = qs('[data-mobile-filter="' + field + '"]');
    if (mSel && mSel.value !== val) mSel.value = val;
    renderList();
  }

  function applySort(val) {
    state.sort = val;
    state.page = 0;
    const d = qs('[data-sort]'); if (d && d.value !== val) d.value = val;
    const m = qs('[data-mobile-sort]'); if (m && m.value !== val) m.value = val;
    renderList();
  }

  function renderFilters() {
    qs('[data-ja-reviews-filters]').hidden = false;
    qsa('.ja-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const set = chip.closest('.ja-chipset');
        const field = set.dataset.chipset;
        const val = chip.dataset['filter' + field.charAt(0).toUpperCase() + field.slice(1)];
        applyFilter(field, val);
      });
    });
    qs('[data-sort]').addEventListener('change', (e) => applySort(e.target.value));
    qsa('[data-mobile-filter]').forEach(sel => {
      sel.addEventListener('change', (e) => applyFilter(sel.dataset.mobileFilter, e.target.value));
    });
    qs('[data-mobile-sort]')?.addEventListener('change', (e) => applySort(e.target.value));
    qs('[data-load-more]').addEventListener('click', () => {
      state.page++;
      renderList(true);
    });
  }

  function filtered() {
    return state.reviews.filter(r => {
      if (state.filters.rating !== 'all') {
        const allowed = state.filters.rating.split(',').map(Number);
        if (!allowed.includes(r.rating)) return false;
      }
      if (state.filters.fit !== 'all' && r.fit !== state.filters.fit) return false;
      if (state.filters.width !== 'all' && r.width !== state.filters.width) return false;
      return true;
    }).sort((a, b) => {
      if (state.sort === 'newest')  return new Date(b.date) - new Date(a.date);
      if (state.sort === 'highest') return b.rating - a.rating;
      if (state.sort === 'lowest')  return a.rating - b.rating;
      if (state.sort === 'media')   return (b.images?.length || 0) - (a.images?.length || 0);
      return 0;
    });
  }

  function renderList(append = false) {
    const list = qs('[data-ja-reviews-list]');
    const loading = qs('[data-ja-reviews-loading]');
    if (loading) loading.remove();
    if (!append) list.innerHTML = '';

    const all = filtered();
    const end = (state.page + 1) * state.pageSize;
    const slice = append ? all.slice(state.page * state.pageSize, end) : all.slice(0, end);

    if (!slice.length) {
      list.innerHTML = '<p class="ja-reviews__empty">No reviews match these filters.</p>';
      qs('[data-ja-reviews-pager]').hidden = true;
      return;
    }

    const tpl = qs('[data-card-template]').content;
    slice.forEach(r => list.appendChild(buildCard(tpl, r)));
    qs('[data-ja-reviews-pager]').hidden = end >= all.length;
  }

  function buildCard(tpl, r) {
    const node = tpl.cloneNode(true);

    // Thumb priority: uploaded review image → hero of the SKU/color actually
    // reviewed (resolved server-side via sibling-map, incl. archived siblings).
    // If neither is available, hide the thumb rather than fall back to the
    // current product's hero — we don't want to misattribute a review to a
    // color it wasn't actually left for.
    const thumb = node.querySelector('[data-thumb]');
    const thumbImg = node.querySelector('[data-thumb-img]');
    const hasUploaded = r.images && r.images.length > 0;
    const thumbSrc = hasUploaded ? r.images[0] : r.hero_image;
    if (thumbSrc) {
      thumbImg.src = thumbSrc;
      thumbImg.alt = hasUploaded ? (r.author + ' photo') : state.productTitle;
      if (hasUploaded) {
        thumb.classList.add('ja-review__thumb--clickable');
        const firstIdxInUGC = state.ugcImages.indexOf(r.images[0]);
        if (firstIdxInUGC >= 0) thumb.addEventListener('click', () => openLightbox(firstIdxInUGC));
      }
    } else {
      thumb.remove();
      node.querySelector('.ja-review').classList.add('ja-review--no-thumb');
    }

    node.querySelector('[data-stars]').textContent = '★'.repeat(r.rating) + '☆'.repeat(5 - r.rating);
    node.querySelector('.ja-review__rating').setAttribute('aria-label', r.rating + ' out of 5 stars');
    const dateText = formatDate(r.date);
    node.querySelector('[data-date]').textContent = dateText;
    const dateInlineEl = node.querySelector('[data-date-inline]');
    if (dateInlineEl) dateInlineEl.textContent = dateText;
    node.querySelector('[data-title]').textContent = r.title || '';
    node.querySelector('[data-author]').textContent = r.author || 'Anonymous';
    const colorEl = node.querySelector('[data-color]');
    if (r.color) colorEl.textContent = r.color;
    else {
      const prevSep = colorEl.previousElementSibling;
      if (prevSep && prevSep.classList.contains('ja-review__sep--color')) prevSep.remove();
      colorEl.remove();
    }
    node.querySelector('[data-body]').textContent = r.body || '';
    const badges = node.querySelector('[data-badges]');
    const verifiedSlot = node.querySelector('[data-verified-slot]');
    if (r.verified && verifiedSlot) verifiedSlot.innerHTML =
      '<span class="ja-review__verified"><span class="ja-review__verified-icon" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 12 12"><path d="M10 3 4.5 8.5 2 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>Verified Purchase</span>';
    if (r.fit && r.fit !== 'tts') badges.insertAdjacentHTML('beforeend',
      '<span class="ja-review__badge ja-review__badge--fit-' + r.fit + '">Fit: ' + fitLabel(r.fit) + '</span>');
    if (r.width && r.width !== 'tts') badges.insertAdjacentHTML('beforeend',
      '<span class="ja-review__badge">Width: ' + widthLabel(r.width) + '</span>');
    if (r.usual_size && r.ordered_size) {
      const delta = Number(r.ordered_size) - Number(r.usual_size);
      if (!Number.isNaN(delta) && delta !== 0) {
        badges.insertAdjacentHTML('beforeend',
          '<span class="ja-review__badge">Sized ' + (delta > 0 ? 'up' : 'down') + ' ' + Math.abs(delta) + '</span>');
      }
    }

    // Additional uploaded images (if more than 1) shown as small strip below body
    if (hasUploaded && r.images.length > 1) {
      const media = node.querySelector('[data-media]');
      media.hidden = false;
      r.images.slice(1).forEach(src => {
        const img = document.createElement('img');
        img.src = src; img.loading = 'lazy'; img.alt = '';
        img.addEventListener('click', () => {
          const idx = state.ugcImages.indexOf(src);
          if (idx >= 0) openLightbox(idx);
        });
        media.appendChild(img);
      });
    }
    return node;
  }

  function fitLabel(v) { return { small: 'runs small', large: 'runs large', tts: 'true to size' }[v] || v; }
  function widthLabel(v) { return { tight: 'runs narrow', loose: 'runs wide', tts: 'true to size' }[v] || v; }
  function formatDate(s) {
    try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return s; }
  }

  function renderEmpty(msg) {
    qs('[data-ja-reviews-loading]')?.remove();
    qs('[data-ja-reviews-list]').innerHTML = '<p class="ja-reviews__empty">' + msg + '</p>';
  }

  qsa('[data-open-review-form]').forEach(btn => btn.addEventListener('click', openReviewForm));

  load();
})();
